// Canonical vocabulary for leader memory visibility.
//
// `private_to_leader` content must never reach coach-facing surfaces or
// coach-facing prompt context. Every coach-side reader of leader_memory_inputs
// must filter through COACH_VISIBLE_LEADER_MEMORY_VISIBILITIES (or the
// predicate) instead of hard-coding the literals — hard-coded copies are how
// visibility vocabularies drift apart.

export const LEADER_MEMORY_VISIBILITY_PRIVATE_TO_LEADER = "private_to_leader" as const;
export const LEADER_MEMORY_VISIBILITY_SHARE_SUMMARY = "share_summary_with_coach" as const;
export const LEADER_MEMORY_VISIBILITY_SHARE_FULL_SOURCE = "share_full_source_with_coach" as const;

export type LeaderMemoryVisibility =
  | typeof LEADER_MEMORY_VISIBILITY_PRIVATE_TO_LEADER
  | typeof LEADER_MEMORY_VISIBILITY_SHARE_SUMMARY
  | typeof LEADER_MEMORY_VISIBILITY_SHARE_FULL_SOURCE;

export const COACH_VISIBLE_LEADER_MEMORY_VISIBILITIES: readonly LeaderMemoryVisibility[] = [
  LEADER_MEMORY_VISIBILITY_SHARE_SUMMARY,
  LEADER_MEMORY_VISIBILITY_SHARE_FULL_SOURCE,
];

export function isCoachVisibleLeaderMemoryVisibility(
  visibility: string | null | undefined,
): boolean {
  return (
    visibility === LEADER_MEMORY_VISIBILITY_SHARE_SUMMARY ||
    visibility === LEADER_MEMORY_VISIBILITY_SHARE_FULL_SOURCE
  );
}

/** Mutable copy for query builders (`.in(...)`) that reject readonly arrays. */
export function coachVisibleLeaderMemoryVisibilityList(): string[] {
  return [...COACH_VISIBLE_LEADER_MEMORY_VISIBILITIES];
}


/** Reserved lossless history; never an active prompt, summary or graph source. */
export const MEMORY_ARCHIVE_PREFIX = "memory_archive:";
export const MEMORY_ARCHIVE_LIKE = `${MEMORY_ARCHIVE_PREFIX.replace(/_/g, "\\_")}%`;

export function isMemoryArchiveType(value: unknown): boolean {
  return typeof value === "string" && value.startsWith(MEMORY_ARCHIVE_PREFIX);
}

export function isActiveCoachMemoryNode(node: { node_type?: string | null }): boolean {
  return !isMemoryArchiveType(node.node_type);
}

export type MemoryProjection = "summary" | "source" | "history";
export type MemoryViewer = { id: string; role: "coach" | "leader" };
/** Build these facts only after authenticated RLS retrieval or a fresh governed RPC. */
export type MemoryReadAuthority = {
  actorId: string;
  kind: "owner_rls" | "explicit_link_rls" | "workspace_rls" | "governed_bi_rpc";
  coachId?: string;
  leaderId?: string;
  audience?: "coach" | "leader";
  proofDigest?: string;
  admittedThemeIds?: readonly string[];
  admittedMomentIds?: readonly string[];
  biProjection?: "owner" | "coach" | "shared";
};
export type MemoryResource = {
  family: "leader_input" | "coach_memory" | "leader_ledger" | "session_record"
    | "carry_forward" | "continuity_review" | "leader_summary" | "governed_bi";
  leaderId?: string | null;
  coachId?: string;
  visibility?: string | null;
  status?: string;
  nodeType?: string | null;
  published?: boolean;
  /** Supplied only from the current source-authorized projection, never cache metadata. */
  publishedSummaryAuthorized?: boolean;
  /** Artifact/extraction source authorization is separate from input row RLS. */
  fullSourceAuthorized?: boolean;
  themeId?: string;
  momentId?: string;
  proofDigest?: string;
};
export type MemoryVisibilityDecision =
  | { allowed: true; projection: MemoryProjection; reason: "admitted" }
  | { allowed: false; projection: null; reason: "invalid_payload" | "authority_missing"
      | "visibility_filtered" | "source_withheld" | "archive_withheld" };

/**
 * Projection defense, not authentication or a database grant. Identity, target
 * and resource-specific authority must come from existing authenticated readers.
 * A row's role, sharing flags or cached proof cannot create this authority.
 */
export function canViewerSee(params: {
  viewer: MemoryViewer;
  authority: MemoryReadAuthority;
  resource: MemoryResource;
  projection: MemoryProjection;
  selectedLeaderId?: string | null;
}): MemoryVisibilityDecision {
  const deny = (reason: Extract<MemoryVisibilityDecision, { allowed: false }>["reason"]): MemoryVisibilityDecision =>
    ({ allowed: false, projection: null, reason });
  if (!params || !params.viewer || !params.authority || !params.resource) return deny("invalid_payload");
  const { viewer, authority, resource, projection } = params;
  if (typeof viewer.id !== "string" || !viewer.id.trim()
    || !["coach", "leader"].includes(viewer.role)
    || !["summary", "source", "history"].includes(projection)) return deny("invalid_payload");
  if (authority.actorId !== viewer.id) return deny("authority_missing");
  const nonemptyId = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;
  const validScope = resource.leaderId === null || nonemptyId(resource.leaderId);
  const ownerLeader = viewer.role === "leader" && viewer.id === resource.leaderId
    && authority.kind === "owner_rls";
  const ownerCoach = viewer.role === "coach" && viewer.id === resource.coachId
    && authority.kind === "owner_rls";
  const relatedCoach = (kind: "explicit_link_rls" | "workspace_rls") => viewer.role === "coach"
    && authority.kind === kind && authority.coachId === viewer.id
    && typeof resource.leaderId === "string" && resource.leaderId.length > 0
    && authority.leaderId === resource.leaderId;
  const allow = (): MemoryVisibilityDecision => ({ allowed: true, projection, reason: "admitted" });
  switch (resource.family) {
    case "leader_input": {
      if (!nonemptyId(resource.leaderId) || !["draft", "approved", "archived", "deleted"].includes(resource.status ?? "")) return deny("invalid_payload");
      if (!["private_to_leader", "share_summary_with_coach", "share_full_source_with_coach"].includes(resource.visibility ?? "")) {
        return deny("invalid_payload");
      }
      if (ownerLeader) return allow();
      if (!relatedCoach("explicit_link_rls")) return deny("authority_missing");
      if (resource.status !== "approved" || !isCoachVisibleLeaderMemoryVisibility(resource.visibility)) return deny("visibility_filtered");
      if (projection === "history") return deny("source_withheld");
      if (projection === "source" && (resource.visibility !== LEADER_MEMORY_VISIBILITY_SHARE_FULL_SOURCE
        || resource.fullSourceAuthorized !== true)) return deny("source_withheld");
      return allow();
    }
    case "coach_memory":
      if (!nonemptyId(resource.coachId) || !validScope || !Object.prototype.hasOwnProperty.call(resource, "leaderId")
        || (resource.nodeType !== null && typeof resource.nodeType !== "string")
        || !Object.prototype.hasOwnProperty.call(resource, "nodeType") || !Object.prototype.hasOwnProperty.call(params, "selectedLeaderId")
        || (params.selectedLeaderId !== null && !nonemptyId(params.selectedLeaderId))) return deny("invalid_payload");
      if (!ownerCoach || !Object.prototype.hasOwnProperty.call(params, "selectedLeaderId")
        || resource.leaderId !== params.selectedLeaderId) return deny("authority_missing");
      if (isMemoryArchiveType(resource.nodeType) && projection !== "history") return deny("archive_withheld");
      return allow();
    case "leader_ledger":
      if (!nonemptyId(resource.leaderId)) return deny("invalid_payload");
      if (!["coach_only", "coach_and_leader"].includes(resource.visibility ?? "")) return deny("invalid_payload");
      // Leader authorship never changes direct coach_only ledger permission.
      if (ownerLeader && resource.visibility === "coach_and_leader") return allow();
      if (relatedCoach("workspace_rls")) return allow(); // Source RLS has already enforced additional source restrictions.
      return deny("visibility_filtered");
    case "session_record":
      if (!nonemptyId(resource.leaderId) || !nonemptyId(resource.coachId) || typeof resource.published !== "boolean") return deny("invalid_payload");
      if (ownerCoach) return allow();
      if (ownerLeader && resource.published === true && projection === "summary") return allow();
      return deny("visibility_filtered");
    case "carry_forward":
      if (!nonemptyId(resource.leaderId) || !nonemptyId(resource.coachId)
        || !["coach_only", "coach_reviewed", "leader_safe", "not_shareable_yet"].includes(resource.visibility ?? "")) return deny("invalid_payload");
      if (relatedCoach("workspace_rls") && viewer.id === resource.coachId) return allow();
      return deny("authority_missing");
    case "continuity_review":
      if (!nonemptyId(resource.coachId) || !validScope || !Object.prototype.hasOwnProperty.call(resource, "leaderId")
        || !["coach_only", "coach_reviewed", "not_shareable_yet"].includes(resource.visibility ?? "")) return deny("invalid_payload");
      // leader_safe in saved coach notes is a review label, not a leader grant.
      if (ownerCoach && resource.leaderId === null) return allow();
      if (relatedCoach("workspace_rls") && viewer.id === resource.coachId) return allow();
      return deny("authority_missing");
    case "leader_summary":
      if (!nonemptyId(resource.leaderId)) return deny("invalid_payload");
      if (ownerLeader) return allow();
      if (relatedCoach("workspace_rls") && resource.publishedSummaryAuthorized === true && projection === "summary") return allow();
      return deny("source_withheld");
    case "governed_bi": {
      if (!nonemptyId(resource.leaderId) || Boolean(resource.themeId) === Boolean(resource.momentId)) return deny("invalid_payload");
      if (authority.kind !== "governed_bi_rpc" || authority.audience !== viewer.role
        || authority.leaderId !== resource.leaderId || viewer.role === "leader" && viewer.id !== resource.leaderId
        || typeof authority.proofDigest !== "string" || !/^[a-f0-9]{64}$/.test(authority.proofDigest)
        || resource.proofDigest !== authority.proofDigest) return deny("authority_missing");
      if (resource.momentId) {
        if (!nonemptyId(resource.momentId) || !Array.isArray(authority.admittedMomentIds)
          || !authority.admittedMomentIds.every(nonemptyId)) return deny("invalid_payload");
        if (!(authority.biProjection === "owner" && viewer.role === "leader"
          || authority.biProjection === "coach" && viewer.role === "coach")
          || !authority.admittedMomentIds.includes(resource.momentId)) return deny("authority_missing");
        return projection === "source" ? allow() : deny("source_withheld");
      }
      if (!nonemptyId(resource.themeId) || !Array.isArray(authority.admittedThemeIds)
        || !authority.admittedThemeIds.every(nonemptyId)) return deny("invalid_payload");
      if (authority.biProjection === "owner" && viewer.role !== "leader"
        || !authority.admittedThemeIds.includes(resource.themeId)) return deny("authority_missing");
      return projection === "summary" ? allow() : deny("source_withheld");
    }
    default:
      return deny("invalid_payload");
  }
}
